import React, { useState } from 'react';

import { Link as RouterLink } from 'react-router-dom';

import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { Theme, Typography, Link, Breadcrumbs, TextField } from '@mui/material';
import VirtualizedList from './VirtualizedList';

import Page from 'components/Page';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            display: 'grid',
            gridTemplateRows: 'auto 1fr',
        },
        top: {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            margin: theme.spacing(1),
        },
        content: {
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gap: theme.spacing(1),
        },
        section: {
            backgroundColor: theme.palette.common.neutralXXLight,
            margin: theme.spacing(1),
            padding: theme.spacing(1),
            minWidth: 900,
            display: 'flex-flow',
        },
        search: {
            width: '100%',
        },
    }),
);

interface ListAlertsPageProps {
    list: React.ReactNode[] | undefined;
    title?: string;
    breadcrumbs?: {
        text: string;
        link?: string;
    }[];
    onSearchChange?: (searchText: string) => void;
    children?: React.ReactNode;
    totalCount?: number;
    loadMore?: () => void;
}

const ListAlertsPage: React.FunctionComponent<ListAlertsPageProps> = ({
    list,
    children,
    title,
    breadcrumbs,
    onSearchChange,
    totalCount,
    loadMore,
}) => {
    const classes = useStyles();

    const [search, setSearch] = useState('');

    const brdcrumbs = breadcrumbs?.map((b) => {
        if (b.link)
            return (
                <Link
                    key={`${b.text}-${b.link}`}
                    color="inherit"
                    component={RouterLink}
                    to={b.link}
                >
                    {b.text}
                </Link>
            );

        return (
            <Typography key={b.text} color="textPrimary">
                {b.text}
            </Typography>
        );
    });

    return (
        <Page className={classes.root}>
            <div className={classes.top}>
                <Breadcrumbs>{brdcrumbs}</Breadcrumbs>
                {title && (
                    <Typography variant="h5" color="textPrimary">
                        {title}
                    </Typography>
                )}
            </div>
            <div className={classes.content}>
                <div className={classes.section}>
                    {onSearchChange && (
                        <TextField
                            variant="outlined"
                            placeholder="Search..."
                            className={classes.search}
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                onSearchChange(e.target.value);
                            }}
                        />
                    )}
                    <div className={classes.content}>
                        {list && totalCount != null && (
                            <VirtualizedList
                                items={list}
                                loadMore={loadMore}
                                totalItemCount={totalCount}
                                searchInput={search}
                            />
                        )}
                    </div>
                    {list && list.length === 0 && (
                        <Typography variant="subtitle1" align="center">
                            0 results found
                        </Typography>
                    )}
                </div>
                <div className={classes.section}>{children}</div>
            </div>
        </Page>
    );
};

ListAlertsPage.displayName = 'ListAlertsPage';
export default ListAlertsPage;
